import { Directive, OnInit, ViewChild, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import { PlanAdminFilterKey, PlanDto } from '@workspace/data-types';
import {
  ActionButton,
  ActionMenu,
  BaseDataTableComponent,
  DataHeaderFilter,
  DataListColumn,
} from '@workspace/ng-aura';
import { Observable } from 'rxjs';

export type PlanColumnKey = 'name' | 'billingCycle' | 'price' | 'isActive' | 'createdAt';

@Directive()
export abstract class AbstractPlansDataTableCoreComponent<T extends PlanDto>
  extends BaseDataTableComponent<T, PlanColumnKey>
  implements OnInit
{
  override topMenu = signal<ActionMenu | null>(null);
  override columns = signal<DataListColumn<PlanColumnKey>[]>([
    { key: 'name', label: 'Nom', display: true },
    { key: 'billingCycle', label: 'Cycle de facturation', display: true },
    { key: 'price', label: 'Tarif', display: true },
    { key: 'isActive', label: 'Statut', display: true },
    { key: 'createdAt', label: 'Date de création', display: false },
  ]);
  override topButtons = signal<ActionButton[]>([]);
  override itemActions = signal<ActionButton[]>([
    {
      label: 'Ouvrir',
      icon: 'open_in_new',
      onClick: (plan: PlanDto) => this.navigateToPlan(plan),
      disabled: false,
      color: 'primary',
    },
  ]);

  override filters = signal<DataHeaderFilter<PlanAdminFilterKey>[]>([
    {
      label: 'Cycle',
      column: PlanAdminFilterKey.BillingCycle,
    },
    {
      label: 'Actif',
      column: PlanAdminFilterKey.IsActive,
    },
  ]);

  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const billingCycle = params.getAll(PlanAdminFilterKey.BillingCycle);
      const isActive = params.getAll(PlanAdminFilterKey.IsActive);
      this.filterChanged({
        changed: true,
        filter: {
          billingCycle: billingCycle.length ? billingCycle : [],
          isActive: isActive.length ? isActive : [],
        },
      });
    });
  }

  navigateToPlan(plan: PlanDto): void {
    if (plan?._id) {
      void this.router.navigate(['/plans', plan._id]);
    }
  }

  abstract override getAll(): Observable<T[]>;
  abstract override countAll(): Observable<number>;

  override getDisplayForEntry(key: PlanAdminFilterKey, rawValue: string): string {
    switch (key) {
      case PlanAdminFilterKey.BillingCycle:
        if (rawValue === 'monthly') return 'Mensuel';
        if (rawValue === 'quarterly') return 'Trimestriel';
        if (rawValue === 'yearly') return 'Annuel';
        return rawValue;
      case PlanAdminFilterKey.IsActive:
        return rawValue === 'true' ? 'Actif' : 'Inactif';
      default:
        return key;
    }
  }
}
